/**
 * The Campaigns screen's rules: what a create sends, what the worker would refuse
 * before it is sent, what each answer means, and whether this caller may write.
 *
 * Out of `Campaigns.tsx` because `node --test` cannot load a `.tsx`, and out of
 * `api.ts` because that module reads `import.meta.env` through `config.ts`. Types
 * come from it as `import type`, which the runner erases.
 *
 * Every list and message here is a copy of the worker's or the contract's.
 * `campaigns.test.ts` reads `apps/worker/src/routes/v1.ts`, `docs/API-CONTRACT.md`
 * and the kit's compiled `canWrite` as text, so a copy that drifts fails.
 *
 * Nothing here grants anything. The worker checks every read and write against
 * ttads' own tables whatever the screen offers; this only decides what to offer.
 */
import type {
	Campaign,
	CampaignBudgetMode,
	CampaignCreate,
	CampaignObjective,
	CampaignSwitch,
	Grant,
	SpecialIndustry,
	Standing,
} from "./api.ts";
import { roleAtLeast } from "./roles.ts";

/** The contract's objectives. `APP_PROMOTION` and `RF_REACH` are refused there, so not offered. */
export const OBJECTIVES = [
	"TRAFFIC",
	"PRODUCT_SALES",
	"WEB_CONVERSIONS",
	"LEAD_GENERATION",
	"REACH",
	"VIDEO_VIEWS",
	"ENGAGEMENT",
] as const satisfies readonly CampaignObjective[];

export const BUDGET_MODES = [
	"BUDGET_MODE_DAY",
	"BUDGET_MODE_TOTAL",
	"BUDGET_MODE_DYNAMIC_DAILY_BUDGET",
	"BUDGET_MODE_INFINITE",
] as const satisfies readonly CampaignBudgetMode[];

export const SPECIAL_INDUSTRIES = [
	"HOUSING",
	"EMPLOYMENT",
	"CREDIT",
] as const satisfies readonly SpecialIndustry[];

/** The worker refuses each of these on presence. No body built here can carry one. */
export const REFUSED_ON_PRESENCE = [
	"appId",
	"appPromotionType",
	"campaignType",
	"budgetOptimizeOn",
	"rtaId",
	"requestId",
	"status",
	"operationStatus",
] as const;

/** Objectives the worker refuses by name, in its words. A Map: the key is typed text. */
const UNREACHABLE_OBJECTIVES: ReadonlyMap<string, string> = new Map([
	[
		"APP_PROMOTION",
		"objectiveType APP_PROMOTION needs app_promotion_type beside it, which v1 does not accept",
	],
	[
		"RF_REACH",
		"objectiveType RF_REACH may not be created DISABLE, and this console always creates DISABLE",
	],
]);

/** What the form holds: text as typed, before anything is decided about it. */
export interface CampaignDraft {
	name: string;
	objectiveType: string;
	budgetMode: string;
	/** As typed. Read as a number once; never rounded, scaled or converted. */
	budget: string;
	specialIndustries: readonly string[];
}

export type Checked<T> = { ok: true; value: T } | { ok: false; error: string };

const refuse = (error: string): { ok: false; error: string } => ({
	ok: false,
	error,
});

/**
 * The create body, or the 400 the worker would answer for it.
 *
 * The checks run in `campaignFromBody`'s order and say what it says, so the form
 * refuses before a request, in the words the API would have used. The body is
 * built field by field from the contract's six; a field refused on presence has
 * no way in. `name` goes as typed, as the worker sends it: a blank one is refused, and
 * nothing typed is trimmed away. `budget` is left out under `BUDGET_MODE_INFINITE`, where it means
 * nothing, and `specialIndustries` when none is ticked: omitted is not sent, and
 * TikTok's own default applies.
 */
export function campaignRequest(advertiser: string, draft: CampaignDraft): Checked<CampaignCreate> {
	if (!advertiser) return refuse("advertiser is required");
	if (!draft.name.trim()) return refuse("name is required");
	if (!draft.objectiveType) return refuse("objectiveType is required");
	const unreachable = UNREACHABLE_OBJECTIVES.get(draft.objectiveType);
	if (unreachable) return refuse(unreachable);

	if (!draft.budgetMode) return refuse("budgetMode is required");
	if (!(BUDGET_MODES as readonly string[]).includes(draft.budgetMode)) {
		return refuse(`budgetMode must be one of ${BUDGET_MODES.join(", ")}`);
	}
	const budgetMode = draft.budgetMode as CampaignBudgetMode;
	let budget: number | undefined;
	if (budgetMode !== "BUDGET_MODE_INFINITE") {
		const typed = draft.budget.trim();
		if (typed === "") return refuse(`budget is required with ${budgetMode}`);
		const read = Number(typed);
		if (!Number.isFinite(read)) return refuse("budget must be a number, in the account's currency");
		budget = read;
	}

	const industries = SPECIAL_INDUSTRIES as readonly string[];
	if (draft.specialIndustries.some((v) => !industries.includes(v))) {
		return refuse(`specialIndustries must be an array of ${SPECIAL_INDUSTRIES.join(", ")}`);
	}

	const value: CampaignCreate = {
		advertiser,
		name: draft.name,
		objectiveType: draft.objectiveType as CampaignObjective,
		budgetMode,
	};
	if (budget !== undefined) value.budget = budget;
	if (draft.specialIndustries.length > 0) {
		value.specialIndustries = [...draft.specialIndustries] as SpecialIndustry[];
	}
	return { ok: true, value };
}

/** What a create's answer means for whoever pressed Create. */
export type CreateOutcome =
	| { kind: "created"; id: string; operationStatus: string }
	/** The campaign exists, whatever else was said. A retry makes a second one. */
	| {
			kind: "exists";
			id: string | null;
			error: string;
			campaign: Campaign | null;
	  }
	/** Nobody can tell whether TikTok made it. */
	| { kind: "unknown"; error: string }
	/** Nothing was made. */
	| { kind: "refused"; status: number; error: string };

const record = (body: unknown): Record<string, unknown> =>
	typeof body === "object" && body !== null && !Array.isArray(body)
		? (body as Record<string, unknown>)
		: {};

const errorOf = (b: Record<string, unknown>, status: number): string =>
	typeof b.error === "string" ? b.error : `the request failed (${status})`;

/**
 * `created` is read before the status. `created: true` means the object exists
 * whatever the status says, and reading it as a refusal is how a second campaign
 * gets made. A 201 that does not name its campaign is still a campaign that exists.
 */
export function createOutcome(status: number, body: unknown): CreateOutcome {
	const b = record(body);
	const id = typeof b.id === "string" ? b.id : null;
	if (b.created === true) {
		const campaign = (b.campaign ?? null) as Campaign | null;
		return { kind: "exists", id, error: errorOf(b, status), campaign };
	}
	if (b.created === "unknown") return { kind: "unknown", error: errorOf(b, status) };
	if (status === 201) {
		return id !== null && typeof b.operationStatus === "string"
			? { kind: "created", id, operationStatus: b.operationStatus }
			: {
					kind: "exists",
					id,
					error: "created, but the answer did not name it",
					campaign: null,
				};
	}
	return { kind: "refused", status, error: errorOf(b, status) };
}

/**
 * A create whose request threw instead of answering: the connection dropped, or
 * something other than the worker replied. The request may still have reached
 * TikTok, so this is "unknown", never "refused".
 */
export function createThrew(message: string): CreateOutcome {
	return { kind: "unknown", error: message };
}

/**
 * Whether Create may be pressed again after this answer. Not after `exists` or
 * `unknown`: the draft is still filled in, and one more click would make a second
 * campaign. The screen keeps Create locked until the list has been read again.
 */
export function mayCreateAgain(outcome: CreateOutcome): boolean {
	return outcome.kind === "created" || outcome.kind === "refused";
}

/**
 * What `GET /campaigns/:id` established: `found`, or `absent` — TikTok answered and
 * does not have it — or `unasked`, which covers both "there was no id to ask with"
 * and "the read did not come back".
 *
 * Whether the *list* answered is not here, because nothing below reads it any more.
 * It was the input this function used to decide on, and deciding on it is the bug
 * (twads#87, mirrored here); a field kept for old times' sake would be the next
 * thing someone reaches for.
 */
export type FoundById = "found" | "absent" | "unasked";

/**
 * The answer still shown after "Reload list".
 *
 * 🔴 **A successful list no longer lifts anything.** A list that came back says
 * nothing about a campaign created seconds ago: X's list was measured lagging
 * behind a create by minutes (twads#86, 2026-09-29), and **for TikTok nobody has
 * measured it at all** — this console has never made a live call. Both readings
 * lead here. Lifting the lock on a list told the operator that a campaign which
 * exists does not, with the draft still filled in and Create enabled: one click
 * from a second campaign with a budget, which v1 cannot delete.
 *
 * What lifts it is the one read that cannot lag behind itself, and only where
 * there is something to read with:
 *
 *   - `exists` carries an id (`routes/v1.ts`, the read-back answers) →
 *     `GET /campaigns/:id` decides.
 *   - `unknown` carries none → **nothing here can lift it.** Reading by id is not
 *     an option in principle, not merely unimplemented: there is no id. The way
 *     out is the deliberate action, not a reload that came back 200.
 *
 * The asymmetry is the uncomfortable part and belongs in the open: the read that
 * settles it is available in the *less* dangerous case and absent in the more
 * dangerous one (d6, twads#87).
 */
export function outcomeAfterReload(
	outcome: CreateOutcome | null,
	byId: FoundById,
): CreateOutcome | null {
	if (outcome === null) return null;
	if (outcome.kind === "exists" && outcome.id !== null) return byId === "found" ? null : outcome;
	// Every other locking answer — `unknown`, or an `exists` whose answer named no id.
	return outcome;
}

/**
 * What a read of `GET /campaigns/:id` established — from the answer's own shape,
 * so a 404 and a failure are not the same word.
 *
 * `absent` is only for TikTok answering that it does not have it. Anything else the
 * console might say — a 502 it could not read through, a 403, a body without the id
 * asked for — is `unasked`: nothing was established, and the lock stays.
 *
 * 🔴 **`absent` has no behaviour of its own, on purpose, and that is not an
 * oversight to tidy up later.** A 404 after an answer that said `created: true`
 * with an id is a contradiction between two things TikTok told us, not the
 * resolution of one: the create named the campaign and the read denies it. Treating
 * it as "so it was not created" would be choosing which answer to believe — and
 * here there is even less to go on than in twads, where one measurement of a
 * non-lagging read by id at least exists. For TikTok there is none. So it lands
 * where `unasked` lands, keeping the lock, and the operator goes to TikTok. It is
 * kept as its own value because the day that contradiction is measured, this is
 * where the answer goes.
 */
export function foundById(status: number, body: unknown, id: string): FoundById {
	if (status === 404) return "absent";
	if (status !== 200) return "unasked";
	return record(body).id === id ? "found" : "unasked";
}

/** What a switch's answer means. */
export type StatusOutcome =
	| {
			kind: "set";
			id: string;
			operationStatus: string;
			secondaryStatus: string | null;
	  }
	/** The write was accepted and TikTok reads back something else. Both are shown. */
	| {
			kind: "mismatch";
			asked: CampaignSwitch;
			operationStatus: string | null;
			secondaryStatus: string | null;
			error: string;
	  }
	| { kind: "refused"; status: number; error: string };

/**
 * The read-back's 502 is told from any other 502 by its fields: only it carries
 * `operationStatus`, `null` when the read-back found no campaign.
 */
export function statusOutcome(asked: CampaignSwitch, status: number, body: unknown): StatusOutcome {
	const b = record(body);
	const secondaryStatus = typeof b.secondaryStatus === "string" ? b.secondaryStatus : null;
	if (status === 200 && typeof b.id === "string" && typeof b.operationStatus === "string") {
		return {
			kind: "set",
			id: b.id,
			operationStatus: b.operationStatus,
			secondaryStatus,
		};
	}
	if (status === 502 && "operationStatus" in b) {
		const operationStatus = typeof b.operationStatus === "string" ? b.operationStatus : null;
		return {
			kind: "mismatch",
			asked,
			operationStatus,
			secondaryStatus,
			error: errorOf(b, status),
		};
	}
	return { kind: "refused", status, error: errorOf(b, status) };
}

/**
 * Whether this caller may create or switch campaigns under one ad account: the
 * kit's `canWrite`, clause for clause and in its order, on what the console holds.
 *
 * 1. A platform `admin` or above holds everything.
 * 2. In an active team, a `member` writes nothing — someone seated on the platform too.
 * 3. With no active team, a platform `user` writes nothing.
 * 4. Otherwise a `write` grant decides: the active team's grants when there is one,
 *    the platform seat's when there is not. Seat grants come from `api.myAccess()`,
 *    since standing does not carry them.
 *
 * The order is the rule. Someone seated on the platform and in an active team is
 * judged by the team, as the kit judges them; reading `standing` first gave them
 * their seat's answer instead. `accountId` is the mirror's uuid, which grants are
 * held on, not the advertiser id. `campaigns.test.ts` runs the kit's own `canWrite`
 * beside this over every combination the console can be given.
 */
export function canWriteAccount(
	standing: Standing,
	platformGrants: readonly Grant[] | null,
	accountId: string,
): boolean {
	const writes = (grants: readonly Grant[]) =>
		grants.some(
			(g) =>
				g.entityType === "tiktok_accounts" &&
				(g.entityId === "*" || g.entityId === accountId) &&
				g.permission === "write",
		);
	const platformRole = standing.platform?.role ?? null;
	if (platformRole !== null && roleAtLeast(platformRole, "admin")) return true;
	const tenant = standing.team?.status === "active" ? standing.team : null;
	if (tenant && tenant.role === "member") return false;
	if (!tenant && platformRole === "user") return false;
	return writes(tenant ? tenant.grants : (platformGrants ?? []));
}
